import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    isWidget,
    getLanguageFromLocale,
    getAffiliateData,
    PROVINCE,
    CREDIT_SCORES,
    AFFILIATE_QUERY_PARAM_KEYS,
} from '@ratehub/base-ui';
import {
    SCENARIOS,
    REWARD_TYPES,
    ANNUAL_FEE_OPTIONS,
    NETWORKS,
    USER_CATEGORIES,
    REWARDS_PROGRAMS,
    DEFAULT_CARD_PREFERENCES,
    getProductType,
    CreditCardSynopsisShape,
    fetchCreditCards,
    createCardSynopsisFromAPIResponse,
    getMonthlySpendingFromAverage,
} from '@ratehub/cc-common';

import { PROVIDERS } from '../definitions/Providers';
import PAGE_TEMPLATES from '../definitions/PageTemplates';
import AdBigbox from './AdBigbox';
import ProductListCreditCards from './ProductListCreditCards';


function ProductTableCreditCardScenario({
    initialScenario = SCENARIOS.REWARDS,
    initialRewardType,
    initialRewardsProgram,
    initialProvinceCode = PROVINCE.ONTARIO,
    initialProvider,
    initialAnnualFee = ANNUAL_FEE_OPTIONS.ANY_FEE,
    initialNetwork = NETWORKS.ALL,
    initialUserCategory,
    initialCards = [],
    initialMonthlySpendingAverage = DEFAULT_CARD_PREFERENCES.monthlySpendingAverage,
    initialMonthlyCarryOverBalance = DEFAULT_CARD_PREFERENCES.lowInterestMonthlyBalance,
    initialTransferredBalance = DEFAULT_CARD_PREFERENCES.balanceTransferAmount,
    maxRowsExpanded,

    hasAd = true,

    customId,

    tableCtaMessage,
    tableCtaHref,
    className,

    ...otherProps
}) {
    const intl = useIntl();
    const isAWidget = isWidget();

    const initialFilterValues = {
        scenario: initialScenario,
        rewardType: initialRewardType,
        rewardsProgram: initialRewardsProgram,
        productType: getProductType(initialScenario === SCENARIOS.REWARDS
            ? initialRewardType
            : initialScenario),
        province: initialProvinceCode ?? PROVINCE.ONTARIO,
        bank: initialProvider,
        noAnnualFee: initialAnnualFee === ANNUAL_FEE_OPTIONS.NO_FEE,
        network: initialNetwork,
        userCategory: initialUserCategory,
        language: getLanguageFromLocale(intl.locale),
        monthlyCarryOverBalance: initialMonthlyCarryOverBalance,
        transferredBalance: initialTransferredBalance,
        creditScore: CREDIT_SCORES.EXCELLENT, // ensures API does not filter by credit score
        income: DEFAULT_CARD_PREFERENCES.income,
        monthlySpendingAverage: initialMonthlySpendingAverage,
        monthlySpending: getMonthlySpendingFromAverage(initialMonthlySpendingAverage),
        requiresGiftOffer: DEFAULT_CARD_PREFERENCES.requiresGiftOffer,

        customId,
    };

    function getFormValuesForFetch(values) {
        return {
            ...initialFilterValues,
            // we want each product table link to the single card eligibility checker to show results that match
            // the product table it is in, so we make that explicit
            productType: getProductType(initialScenario === SCENARIOS.REWARDS
                ? values.rewardType ?? initialRewardType
                : initialScenario),
            rewardType: values.rewardType,
            province: values.location?.province?.code ?? PROVINCE.ONTARIO,
            noAnnualFee: values.annualFee === ANNUAL_FEE_OPTIONS.NO_FEE,
            creditScore: values.creditScore,
            income: values.income,
            monthlySpending: getMonthlySpendingFromAverage(values.monthlySpendingAverage),
            monthlySpendingAverage: values.monthlySpendingAverage,
            monthlyCarryOverBalance: values.monthlyCarryOverBalance,
            transferredBalance: values.transferredBalance,
            sortBy: values.sortBy,
            requiresGiftOffer: values.requiresGiftOffer,
        };
    }

    return (
        <div className={classNames(className, 'rh-display-flex rh-flex-direction-column rh-justify-content-space-around')}>
            <div className="rh-flex-grow-1">
                <ProductListCreditCards
                    ctaMessage={tableCtaMessage}
                    ctaLink={tableCtaHref}

                    maxRowsExpanded={maxRowsExpanded}

                    initialRewardType={initialRewardType}
                    initialFetchedCards={initialCards}
                    initialFilterValues={initialFilterValues}
                    initialUserCategory={initialUserCategory}
                    getFormValues={getFormValuesForFetch}
                    onFetchValue={fetchProductListCreditCards}
                    initialProvinceCode={initialProvinceCode}

                    {...otherProps}
                />
            </div>

            {/* Might have other things in the future - for now, just AdBigbox */}
            <If condition={hasAd && !isAWidget}>
                <aside className={
                    classNames('sidebar rh-position-relative rh-display-flex rh-align-items-flex-start',
                        'rh-justify-content-center rh-pt-4 rh-pb-1 rh-mx-1_5')}
                >
                    <AdBigbox alignment={AdBigbox.ALIGNMENTS.LEFT} />
                </aside>
            </If>
        </div>
    );
}

ProductTableCreditCardScenario.propTypes = {
    initialProvinceCode: PropTypes.string,
    initialScenario: PropTypes.oneOf(Object.values(SCENARIOS)),
    initialRewardType: PropTypes.oneOf(Object.values(REWARD_TYPES)),
    initialRewardsProgram: PropTypes.oneOf(Object.values(REWARDS_PROGRAMS)),
    initialAnnualFee: PropTypes.oneOf(Object.values(ANNUAL_FEE_OPTIONS)),
    initialProvider: PropTypes.oneOf(PROVIDERS),
    initialNetwork: PropTypes.oneOf(Object.values(NETWORKS)),
    initialUserCategory: PropTypes.oneOf(Object.values(USER_CATEGORIES)),
    initialCards: PropTypes.arrayOf(PropTypes.shape(CreditCardSynopsisShape)),
    initialMonthlySpendingAverage: PropTypes.number,
    initialMonthlyCarryOverBalance: PropTypes.number,
    initialTransferredBalance: PropTypes.number,
    maxRowsExpanded: PropTypes.number,

    customId: PropTypes.string,

    tableCtaMessage: PropTypes.string,
    tableCtaHref: PropTypes.string,
    className: PropTypes.string,

    disableFee: PropTypes.bool,
    hasAd: PropTypes.bool,
};



async function fetchProductListCreditCards(values) {
    // TODO in the future, we might avoid calling fetchCreditCards() again if the only properties
    // that changed are monthlyCarryOverBalance or transferredBalance
    const response = (await fetchCreditCards({
        ...values,
        customId: getAffiliateData()?.[AFFILIATE_QUERY_PARAM_KEYS.affiliateId] || values.customId,
        prioritizeSponsored: true,
    })).creditCards;

    return response
        ? response
            .map(card =>
                createCardSynopsisFromAPIResponse(card, {
                    monthlySpendingAverage: values.monthlySpendingAverage,
                    lowInterestMonthlyBalance: values.monthlyCarryOverBalance,
                    balanceTransferAmount: values.transferredBalance,
                }),
            )
        : [];
}

const MEDIA_QUERY_SIZE = '76.625em';

ProductTableCreditCardScenario.blockKey = 'rh/credit-card-rate-table';
ProductTableCreditCardScenario.requiresLayoutRow = true;
ProductTableCreditCardScenario.supportedPageTypes = [ PAGE_TEMPLATES.creditCards ];

export default styled(observer(ProductTableCreditCardScenario))`
    @media screen and (min-width: ${MEDIA_QUERY_SIZE}) {
        flex-direction: row;
    }

    > .sidebar {
        flex-basis: 300px;
    }
`;
